/**
 * In-canvas widget kit drawn with the game's own UI pieces (button DC6s, fonts).
 */
import type { Font } from '../assets/font';
import { TextColor } from '../assets/store';
import type { Sprite } from '../gfx/atlas';
import type { Renderer } from '../gfx/renderer';
import type { InputSink, MouseButton } from '../engine/input';

export abstract class Widget {
  visible = true;
  enabled = true;
  constructor(
    public x: number,
    public y: number,
    public w: number,
    public h: number,
  ) {}
  hit(px: number, py: number): boolean {
    return this.visible && px >= this.x && py >= this.y && px < this.x + this.w && py < this.y + this.h;
  }
  abstract render(r: Renderer, t: number): void;
  onMouseDown?(x: number, y: number, b: MouseButton): void;
  onMouseUp?(x: number, y: number, b: MouseButton, inside: boolean): void;
  onMouseMove?(x: number, y: number): void;
  onWheel?(dy: number): void;
  onKeyDown?(e: KeyboardEvent): boolean;
  onText?(t: string): void;
  focusable = false;
  focused = false;
}

/** Layout of a D2 button graphic: frames are `segments` wide, normal then pressed (then disabled). */
export interface ButtonStyle {
  sprites: Sprite[];
  segments: number;
  font: Font;
  textColor?: TextColor;
  /** Frame set index used when disabled, or -1 to dim the normal frames. */
  disabledSet?: number;
  textDy?: number;
  /** Palette the art is drawn with (the Battle.net lobby's buttons use the Act 1 palette). */
  palette?: number;
}

export class Button extends Widget {
  pressed = false;
  toggled = false;
  constructor(
    x: number,
    y: number,
    private readonly style: ButtonStyle,
    public label: string,
    public onClick: () => void,
    private readonly sound?: () => void,
  ) {
    let w = 0;
    for (let i = 0; i < style.segments; i++) w += style.sprites[i]?.width ?? 0;
    super(x, y, w, style.sprites[0]?.height ?? 0);
  }
  render(r: Renderer): void {
    if (!this.visible) return;
    const s = this.style;
    const down = (this.pressed || this.toggled) && this.enabled;
    let set = down ? 1 : 0;
    if (!this.enabled && s.disabledSet !== undefined && s.disabledSet >= 0) set = s.disabledSet;
    let x = this.x;
    for (let i = 0; i < s.segments; i++) {
      const spr = s.sprites[set * s.segments + i] ?? s.sprites[i];
      if (!spr) continue;
      r.draw(spr, x, this.y, { topLeft: true, palette: s.palette, tint: !this.enabled && (s.disabledSet ?? -1) < 0 ? [0.5, 0.5, 0.5] : undefined });
      x += spr.width;
    }
    if (this.label) {
      const f = s.font;
      // Long labels wrap onto two lines, as the original's tall buttons do.
      const lines = f.width(this.label) > this.w - 12 ? f.lines(this.label, this.w - 12) : [this.label];
      const lh = f.lineHeight + 2;
      const ty = this.y + Math.round((this.h - lines.length * lh) / 2) + (s.textDy ?? 0) + (down ? 1 : 0);
      lines.forEach((line, i) =>
        f.draw(r, line, this.x + this.w / 2 - (down ? 1 : 0), ty + i * lh, {
          align: 'center',
          color: this.enabled ? (s.textColor ?? TextColor.Black) : TextColor.Grey,
        }),
      );
    }
  }
  override onMouseDown(): void {
    if (this.enabled) this.pressed = true;
  }
  override onMouseUp(_x: number, _y: number, _b: MouseButton, inside: boolean): void {
    const was = this.pressed;
    this.pressed = false;
    if (was && inside && this.enabled) {
      this.sound?.();
      this.onClick();
    }
  }
}

export class Label extends Widget {
  constructor(
    x: number,
    y: number,
    public text: string,
    private readonly font: Font,
    public color: TextColor = TextColor.White,
    public align: 'left' | 'center' | 'right' = 'left',
    public wrap?: number,
  ) {
    super(x, y, 0, 0);
  }
  render(r: Renderer): void {
    if (this.visible && this.text) this.font.draw(r, this.text, this.x, this.y, { color: this.color, align: this.align, wrap: this.wrap });
  }
}

/** Single-line text entry with caret, selection-free editing and optional masking. */
export class TextBox extends Widget {
  text = '';
  onEnter?: () => void;
  onChange?: (t: string) => void;
  override focusable = true;
  constructor(
    x: number,
    y: number,
    w: number,
    h: number,
    private readonly font: Font,
    private readonly bg: Sprite | null,
    public maxLength = 15,
    public mask = false,
    public filter: RegExp | null = null,
    public color: TextColor = TextColor.White,
  ) {
    super(x, y, w, h);
  }
  render(r: Renderer, t: number): void {
    if (!this.visible) return;
    if (this.bg) r.draw(this.bg, this.x, this.y, { topLeft: true });
    const shown = this.mask ? '*'.repeat(this.text.length) : this.text;
    const ty = this.y + Math.round((this.h - this.font.lineHeight) / 2) + 1;
    // Keep the caret in view for long text.
    let start = 0;
    while (this.font.width(shown.slice(start)) > this.w - 14 && start < shown.length) start++;
    const visible = shown.slice(start);
    this.font.draw(r, visible, this.x + 6, ty, { color: this.color });
    if (this.focused && Math.floor(t * 2.5) % 2 === 0) {
      const cx = this.x + 6 + this.font.width(visible);
      this.font.draw(r, '_', cx, ty, { color: this.color });
    }
  }
  override onText(ch: string): void {
    if (this.text.length >= this.maxLength) return;
    if (this.filter && !this.filter.test(ch)) return;
    this.text += ch;
    this.onChange?.(this.text);
  }
  override onKeyDown(e: KeyboardEvent): boolean {
    if (e.key === 'Backspace') {
      this.text = e.ctrlKey || e.altKey ? '' : this.text.slice(0, -1);
      this.onChange?.(this.text);
      return true;
    }
    if (e.key === 'Enter') {
      this.onEnter?.();
      return true;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      void navigator.clipboard?.readText().then((s) => {
        for (const ch of s) this.onText(ch);
      });
      return true;
    }
    return false;
  }
}

/** Scrollable list of text rows with a selection (game list, channel list, character list…). */
export class ListBox<T> extends Widget {
  items: T[] = [];
  /** Selected row's bar (the Battle.net lists use a grey one). */
  highlight: [number, number, number, number] = [0.55, 0.42, 0.18, 0.35];
  selected = -1;
  scroll = 0;
  onSelect?: (item: T, index: number) => void;
  onActivate?: (item: T, index: number) => void;
  private lastClick = 0;
  constructor(
    x: number,
    y: number,
    w: number,
    h: number,
    private readonly font: Font,
    private readonly rowHeight: number,
    private readonly renderRow: (r: Renderer, item: T, x: number, y: number, w: number, selected: boolean) => void,
  ) {
    super(x, y, w, h);
  }
  get rowsVisible(): number {
    return Math.floor(this.h / this.rowHeight);
  }
  render(r: Renderer): void {
    if (!this.visible) return;
    r.setClip([this.x, this.y, this.w, this.h]);
    const n = this.rowsVisible;
    for (let i = 0; i < n; i++) {
      const idx = this.scroll + i;
      if (idx >= this.items.length) break;
      const y = this.y + i * this.rowHeight;
      if (idx === this.selected) r.fillRect(this.x, y, this.w, this.rowHeight, this.highlight);
      this.renderRow(r, this.items[idx], this.x + 4, y, this.w - 8, idx === this.selected);
    }
    r.setClip(null);
  }
  override onMouseDown(_x: number, y: number): void {
    const idx = this.scroll + Math.floor((y - this.y) / this.rowHeight);
    if (idx < 0 || idx >= this.items.length) return;
    const now = performance.now();
    const dbl = idx === this.selected && now - this.lastClick < 350;
    this.selected = idx;
    this.lastClick = now;
    this.onSelect?.(this.items[idx], idx);
    if (dbl) this.onActivate?.(this.items[idx], idx);
  }
  override onWheel(dy: number): void {
    this.scroll = Math.max(0, Math.min(Math.max(0, this.items.length - this.rowsVisible), this.scroll + dy * 2));
  }
  get fontRef(): Font {
    return this.font;
  }
}

/** Routes input to child widgets, tracks focus and the pressed widget. */
export class WidgetLayer implements InputSink {
  readonly widgets: Widget[] = [];
  private pressed: Widget | null = null;
  focus: Widget | null = null;
  add<W extends Widget>(w: W): W {
    this.widgets.push(w);
    return w;
  }
  remove(w: Widget): void {
    const i = this.widgets.indexOf(w);
    if (i >= 0) this.widgets.splice(i, 1);
  }
  render(r: Renderer, t: number): void {
    for (const w of this.widgets) if (w.visible) w.render(r, t);
  }
  setFocus(w: Widget | null): void {
    if (this.focus) this.focus.focused = false;
    this.focus = w;
    if (w) w.focused = true;
  }
  private at(x: number, y: number): Widget | null {
    for (let i = this.widgets.length - 1; i >= 0; i--) {
      const w = this.widgets[i];
      if (w.enabled && w.hit(x, y)) return w;
    }
    return null;
  }
  onMouseDown(x: number, y: number, b: MouseButton): boolean {
    const w = this.at(x, y);
    this.pressed = w;
    if (w?.focusable) this.setFocus(w);
    w?.onMouseDown?.(x, y, b);
    return !!w;
  }
  onMouseUp(x: number, y: number, b: MouseButton): boolean {
    const w = this.pressed;
    this.pressed = null;
    w?.onMouseUp?.(x, y, b, w.hit(x, y));
    return !!w;
  }
  onMouseMove(x: number, y: number): void {
    this.pressed?.onMouseMove?.(x, y);
  }
  onWheel(dy: number, e: WheelEvent): void {
    void e;
    for (const w of this.widgets) if (w.onWheel && w.hit(lastX, lastY)) w.onWheel(dy);
  }
  onKeyDown(e: KeyboardEvent): boolean {
    if (e.key === 'Tab') {
      const f = this.widgets.filter((w) => w.focusable && w.visible && w.enabled);
      if (f.length) {
        const i = this.focus ? f.indexOf(this.focus) : -1;
        this.setFocus(f[(i + (e.shiftKey ? f.length - 1 : 1)) % f.length]);
      }
      return true;
    }
    return this.focus?.onKeyDown?.(e) ?? false;
  }
  onText(t: string): void {
    this.focus?.onText?.(t);
  }
}

let lastX = 0;
let lastY = 0;
/** Lets wheel events find the widget under the pointer. */
export function trackPointer(x: number, y: number): void {
  lastX = x;
  lastY = y;
}
